import { useEffect, useMemo, useState } from "react";

import { createTaskV2, getProjects, getTaskUserSuggestions } from "@/lib/api";
import { parseTaggingReferences } from "@/lib/tagging";
import { TaggingInput } from "@/components/TaggingInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const GENERAL_TASK_PROJECT_ID = "__general__";
const GENERAL_TASK_PROJECT_OPTION = {
  id: GENERAL_TASK_PROJECT_ID,
  name: "General (Non-project)",
  client_name: "Internal",
  project_admin_email: "",
};

export const AddTaskModal = ({
  open,
  onOpenChange,
  user,
  defaultProjectId = "",
  onTaskCreated,
}) => {
  const [projects, setProjects] = useState([]);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taskTitleMarkup, setTaskTitleMarkup] = useState("");
  const [draft, setDraft] = useState({
    project_id: defaultProjectId || GENERAL_TASK_PROJECT_ID,
    assigned_to_email: "",
    deadline: "",
    priority: "medium",
    notes: "",
  });

  const canCreateTask = Boolean(user?.email);

  const taggedRefs = useMemo(() => parseTaggingReferences(taskTitleMarkup), [taskTitleMarkup]);

  useEffect(() => {
    if (!open || !canCreateTask) return;

    const loadMeta = async () => {
      try {
        const [projectRows, userRows] = await Promise.all([
          getProjects(),
          getTaskUserSuggestions({ q: "", workspace_domain: user.workspace_domain }),
        ]);
        const hasGeneral = projectRows.some((project) => project.id === GENERAL_TASK_PROJECT_ID);
        setProjects(hasGeneral ? projectRows : [GENERAL_TASK_PROJECT_OPTION, ...projectRows]);
        setWorkspaceUsers((userRows || []).map((row) => ({
          name: row.name || row.email,
          email: row.email,
          role: row.role,
        })));
      } catch (error) {
        toast.error(error.message);
      }
    };

    loadMeta();
  }, [open, canCreateTask]);

  useEffect(() => {
    if (!open) return;
    setDraft((prev) => ({
      ...prev,
      project_id: defaultProjectId || prev.project_id || GENERAL_TASK_PROJECT_ID,
    }));
  }, [defaultProjectId, open]);

  useEffect(() => {
    const taggedProjectId = taggedRefs.lastProjectMention?.id;
    if (taggedProjectId) {
      setDraft((prev) => ({ ...prev, project_id: taggedProjectId }));
    }

    const taggedAssigneeEmail = taggedRefs.lastUserMention?.id;
    if (taggedAssigneeEmail && !(draft.project_id === GENERAL_TASK_PROJECT_ID && draft.assigned_to_email === "__all__")) {
      setDraft((prev) => ({ ...prev, assigned_to_email: taggedAssigneeEmail }));
    }
  }, [taggedRefs.lastProjectMention?.id, taggedRefs.lastUserMention?.id, draft.project_id, draft.assigned_to_email]);

  useEffect(() => {
    if (!open) {
      setTaskTitleMarkup("");
      setDraft({
        project_id: defaultProjectId || GENERAL_TASK_PROJECT_ID,
        assigned_to_email: "",
        deadline: "",
        priority: "medium",
        notes: "",
      });
    }
  }, [defaultProjectId, open]);

  const assigneeOptions = useMemo(() => {
    const options = [...workspaceUsers];

    const taggedAssigneeEmail = taggedRefs.lastUserMention?.id;
    if (taggedAssigneeEmail && !options.some((option) => option.email === taggedAssigneeEmail)) {
      options.unshift({
        name: taggedAssigneeEmail,
        email: taggedAssigneeEmail,
        role: "mention",
      });
    }
    return options;
  }, [taggedRefs.lastUserMention?.id, workspaceUsers]);

  useEffect(() => {
    if (!open) return;
    if (!assigneeOptions.length) return;

    const hasCurrent = assigneeOptions.some((option) => option.email === draft.assigned_to_email)
      || (draft.project_id === GENERAL_TASK_PROJECT_ID && draft.assigned_to_email === "__all__");
    if (!draft.assigned_to_email || !hasCurrent) {
      setDraft((prev) => ({ ...prev, assigned_to_email: assigneeOptions[0].email }));
    }
  }, [assigneeOptions, draft.assigned_to_email, open, user.role]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canCreateTask) {
      toast.error("You do not have permission to create tasks");
      return;
    }

    if (!taggedRefs.title) {
      toast.error("Task title is required");
      return;
    }
    if (!draft.deadline) {
      toast.error("Deadline is required");
      return;
    }

    const assignToAll = draft.project_id === GENERAL_TASK_PROJECT_ID && draft.assigned_to_email === "__all__";
    const fallbackAssignee = assignToAll ? undefined : (draft.assigned_to_email || undefined);
    const assigneeEmail = assignToAll ? undefined : (taggedRefs.lastUserMention?.id || fallbackAssignee);

    setIsSubmitting(true);
    try {
      await createTaskV2({
        project_id: draft.project_id || GENERAL_TASK_PROJECT_ID,
        task: taggedRefs.title,
        assigned_to_email: assigneeEmail,
        assign_to_all: assignToAll,
        deadline: draft.deadline,
        priority: draft.priority,
        notes: draft.notes,
      });
      toast.success("Task created");
      onOpenChange(false);
      onTaskCreated?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="add-task-modal">
        <DialogHeader>
          <DialogTitle data-testid="add-task-modal-title">Create New Task</DialogTitle>
          <DialogDescription data-testid="add-task-modal-description">
            Use @ to tag people and # to tag a project. You can also use General (Non-project).
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit} data-testid="add-task-modal-form">
          <div className="space-y-1.5">
            <Label htmlFor="add-task-modal-title-input">Task title</Label>
            <TaggingInput
              value={taskTitleMarkup}
              onChange={setTaskTitleMarkup}
              workspaceDomain={user.workspace_domain}
              placeholder="Write task with @person and optional #project"
              testId="add-task-modal-title-input"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select value={draft.project_id} onValueChange={(value) => setDraft((prev) => ({ ...prev, project_id: value }))}>
                <SelectTrigger data-testid="add-task-modal-project-select">
                  <SelectValue placeholder="General (Non-project)" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id} data-testid={`add-task-modal-project-option-${project.id}`}>
                      <div className="flex w-full items-center justify-between gap-2" data-testid={`add-task-modal-project-option-content-${project.id}`}>
                        <span className="truncate">{project.name}</span>
                        <span
                          className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                          data-testid={`add-task-modal-project-option-client-pill-${project.id}`}
                        >
                          {project.client_name || "Internal"}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Assign to</Label>
              <Select
                value={draft.assigned_to_email || ""}
                onValueChange={(value) => setDraft((prev) => ({ ...prev, assigned_to_email: value }))}
              >
                <SelectTrigger data-testid="add-task-modal-assignee-select">
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  {draft.project_id === GENERAL_TASK_PROJECT_ID ? (
                    <SelectItem value="__all__" data-testid="add-task-modal-assignee-option-all">
                      Assign to all (fan-out)
                    </SelectItem>
                  ) : null}
                  {assigneeOptions.map((option) => (
                    <SelectItem
                      key={option.email}
                      value={option.email}
                      data-testid={`add-task-modal-assignee-option-${option.email.replace(/[@.]/g, "-")}`}
                    >
                      {option.name || option.email} · {option.email} ({String(option.role || "member").replace("_", " ")})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="add-task-modal-deadline-input">Deadline</Label>
              <Input
                id="add-task-modal-deadline-input"
                type="date"
                value={draft.deadline}
                onChange={(event) => setDraft((prev) => ({ ...prev, deadline: event.target.value }))}
                min={new Date().toISOString().slice(0, 10)}
                data-testid="add-task-modal-deadline-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={draft.priority} onValueChange={(value) => setDraft((prev) => ({ ...prev, priority: value }))}>
                <SelectTrigger data-testid="add-task-modal-priority-select">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low" data-testid="add-task-modal-priority-low">Low</SelectItem>
                  <SelectItem value="medium" data-testid="add-task-modal-priority-medium">Medium</SelectItem>
                  <SelectItem value="high" data-testid="add-task-modal-priority-high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Status</Label>
              <div className="field-input" style={{ height: 40, display: "inline-flex", alignItems: "center", width: "100%" }} data-testid="add-task-modal-status-fixed-chip">Open</div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-task-modal-notes-input">Notes (optional)</Label>
            <Input
              id="add-task-modal-notes-input"
              value={draft.notes}
              onChange={(event) => setDraft((prev) => ({ ...prev, notes: event.target.value }))}
              placeholder="Task details"
              data-testid="add-task-modal-notes-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="add-task-modal-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} data-testid="add-task-modal-submit-button">
              {isSubmitting ? "Creating..." : "Create Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
